import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { CreditCard, Zap, Clock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter 234.c (Carlos) — Dropdown bulk para asignar forma de pago a N facturas.
 *
 * Si la forma es "instantánea" (contado/tarjeta/transferencia/cheque al día):
 *   → las facturas se marcan PAGADAS en el ERP y se sacan de la nómina.
 * Si es crédito o cheque diferido:
 *   → quedan PENDIENTES esperando el viernes de pago correspondiente.
 */
export default function BatchFormaPagoDropdown({ selectedIds, disabled, onApplied }) {
  const [opciones, setOpciones] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/flujo-caja/cxp/formas-pago')
      .then((r) => setOpciones(r.data?.items || []))
      .catch(() => setOpciones([]));
  }, []);

  const apply = async (forma_pago) => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const res = await api.patch('/flujo-caja/cxp/forma-pago/bulk', {
        factura_ids: selectedIds,
        forma_pago,
      });
      const d = res.data || {};
      const label = opciones.find((o) => o.key === forma_pago)?.label || forma_pago;
      toast.success(
        d.instantanea
          ? `${d.marcadas_pagadas} factura(s) marcadas pagadas (${label})`
          : `Forma de pago "${label}" asignada a ${d.modified} factura(s)`
      );
      onApplied?.();
    } catch (e) {
      toast.error(`Error: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setBusy(false);
    }
  };

  const instantaneas = opciones.filter((o) => o.instantanea);
  const credito = opciones.filter((o) => !o.instantanea);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          disabled={disabled || busy || !selectedIds.length}
          data-testid="btn-batch-forma-pago"
        >
          <CreditCard className="w-3.5 h-3.5 mr-1" />
          Forma de pago
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-amber-500" />
          Salida inmediata (marca PAGADA)
        </DropdownMenuLabel>
        {instantaneas.map((o) => (
          <DropdownMenuItem
            key={o.key}
            onClick={() => apply(o.key)}
            className="text-xs"
            data-testid={`forma-pago-${o.key}`}
          >
            {o.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-sky-500" />
          Va a Nómina de Viernes
        </DropdownMenuLabel>
        {credito.map((o) => (
          <DropdownMenuItem
            key={o.key}
            onClick={() => apply(o.key)}
            className="text-xs"
            data-testid={`forma-pago-${o.key}`}
          >
            {o.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
